/**
 * Training profile AI import modal.
 * Provides a whole-profile JSON import surface with AI-friendly reference data,
 * configuration catalog guidance, and preview/validation before applying the import.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Collapse,
  Input,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  AimOutlined,
  AppstoreOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  DownloadOutlined,
  FormatPainterOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import Editor, { loader } from '@monaco-editor/react';
import trainingProfilesService from '../../services/trainingProfilesService';
import styles from './TrainingProfileBulkImportModal.module.css';
import {
  CONDITION_COMBINATOR_DEFINITIONS,
  CONDITION_FIELD_DEFINITIONS,
  CONDITION_OPERATOR_DEFINITIONS,
  SPOTLIGHT_POSITION_DEFINITIONS,
  buildTrainingProfileAiContext,
  buildTrainingProfileAiInstructions,
  generateTrainingProfileCurrentImportJson,
  generateTrainingProfileImportTemplate,
  parseTrainingProfileImportText,
} from './trainingProfileAiImportUtils';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const MONACO_INIT_TIMEOUT_MS = 10000;
const MONACO_MOUNT_TIMEOUT_MS = 10000;

const getQuestArrayKey = (parsedValue) => {
  if (Array.isArray(parsedValue)) {
    return null;
  }

  if (Array.isArray(parsedValue?.TutorialQuests)) {
    return 'TutorialQuests';
  }

  if (Array.isArray(parsedValue?.tutorialQuests)) {
    return 'tutorialQuests';
  }

  if (Array.isArray(parsedValue?.Quests)) {
    return 'Quests';
  }

  if (Array.isArray(parsedValue?.quests)) {
    return 'quests';
  }

  return null;
};

const findNthObjectOffsetRangeInArray = (jsonString, arrayStartIndex, itemIndex) => {
  let inString = false;
  let escapeNext = false;
  let arrayDepth = 1;
  let objectDepth = 0;
  let currentIndex = -1;
  let itemStartOffset = null;

  for (let index = arrayStartIndex + 1; index < jsonString.length; index += 1) {
    const char = jsonString[index];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\' && inString) {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) {
      continue;
    }

    if (char === '[') {
      arrayDepth += 1;
      continue;
    }

    if (char === ']') {
      arrayDepth -= 1;
      if (arrayDepth === 0) {
        return null;
      }
      continue;
    }

    if (char === '{') {
      if (arrayDepth === 1 && objectDepth === 0) {
        currentIndex += 1;
        if (currentIndex === itemIndex) {
          itemStartOffset = index;
        }
      }
      objectDepth += 1;
      continue;
    }

    if (char === '}') {
      objectDepth -= 1;
      if (arrayDepth === 1 && objectDepth === 0 && currentIndex === itemIndex && itemStartOffset !== null) {
        return {
          startOffset: itemStartOffset,
          endOffsetExclusive: index + 1,
        };
      }
    }
  }

  return null;
};

const findQuestOffsetRange = (jsonString, parsedValue, questIndex) => {
  if (!jsonString || questIndex < 0) {
    return null;
  }

  if (Array.isArray(parsedValue)) {
    const arrayStartIndex = jsonString.indexOf('[');
    return arrayStartIndex >= 0 ? findNthObjectOffsetRangeInArray(jsonString, arrayStartIndex, questIndex) : null;
  }

  const questArrayKey = getQuestArrayKey(parsedValue);
  if (!questArrayKey) {
    return null;
  }

  const keyIndex = jsonString.indexOf(`"${questArrayKey}"`);
  if (keyIndex < 0) {
    return null;
  }

  const arrayStartIndex = jsonString.indexOf('[', keyIndex);
  if (arrayStartIndex < 0) {
    return null;
  }

  return findNthObjectOffsetRangeInArray(jsonString, arrayStartIndex, questIndex);
};

/**
 * Whole-profile modal for importing AI-authored training profile JSON.
 * @param {object} props Component props.
 * @param {boolean} props.open Whether the modal is open.
 * @param {number} props.profileId Current training profile ID.
 * @param {object|null} props.profile Current editor profile snapshot.
 * @param {Array<object>} props.countries Available countries for country resolution.
 * @param {Array<object>} props.quizCollections Available quiz collections for assignment resolution.
 * @param {Function} props.onClose Invoked when the modal closes.
 * @param {Function} props.onSuccess Invoked after a successful import.
 * @returns {JSX.Element} AI import modal.
 */
const TrainingProfileAiImportModal = ({
  open,
  profileId,
  profile,
  countries,
  quizCollections,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);

  const [jsonValue, setJsonValue] = useState('');
  const [importing, setImporting] = useState(false);
  const [editorLoadState, setEditorLoadState] = useState('idle');
  const [editorRenderKey, setEditorRenderKey] = useState(0);
  const [selectedQuestIndex, setSelectedQuestIndex] = useState(null);

  const importContext = useMemo(() => ({ profile, countries, quizCollections }), [countries, profile, quizCollections]);
  const parseResult = useMemo(() => parseTrainingProfileImportText(jsonValue, importContext), [importContext, jsonValue]);
  const aiContextValue = useMemo(() => JSON.stringify(buildTrainingProfileAiContext(importContext), null, 2), [importContext]);
  const aiInstructions = useMemo(() => buildTrainingProfileAiInstructions(), []);
  const combinedAiPackage = useMemo(() => `${aiInstructions}\n\nAI reference JSON:\n${aiContextValue}`, [aiContextValue, aiInstructions]);

  const validQuestCount = useMemo(
    () => parseResult.questValidationResults.filter((result) => result.valid).length,
    [parseResult.questValidationResults]
  );

  const invalidQuestCount = useMemo(
    () => parseResult.questValidationResults.filter((result) => !result.valid).length,
    [parseResult.questValidationResults]
  );

  const totalErrorCount = parseResult.profileErrors.length + invalidQuestCount;
  const canImport = Boolean(profileId) && parseResult.isValid && parseResult.request;

  const resetEditorState = useCallback(() => {
    editorRef.current = null;
    setEditorLoadState('idle');
    setEditorRenderKey((currentKey) => currentKey + 1);
  }, []);

  useEffect(() => {
    if (!open) {
      setJsonValue('');
      setImporting(false);
      setSelectedQuestIndex(null);
      resetEditorState();
    }
  }, [open, resetEditorState]);

  useEffect(() => {
    let isActive = true;
    let timeoutId = null;

    if (!open || editorLoadState !== 'loading') {
      return undefined;
    }

    const initPromise = import('monaco-editor').then((monacoModule) => {
      const monaco = monacoModule.default ?? monacoModule;
      loader.config({ monaco });
      return loader.init();
    });

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`Monaco initialization timed out after ${MONACO_INIT_TIMEOUT_MS}ms`));
      }, MONACO_INIT_TIMEOUT_MS);
    });

    Promise.race([initPromise, timeoutPromise])
      .then(() => {
        if (isActive) {
          setEditorLoadState('ready');
        }
      })
      .catch((error) => {
        console.error('Monaco initialization failed; using textarea fallback.', error);
        if (isActive) {
          setEditorLoadState('failed');
        }
      })
      .finally(() => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      });

    return () => {
      isActive = false;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [editorLoadState, open]);

  useEffect(() => {
    let timeoutId = null;

    if (!open || editorLoadState !== 'ready' || editorRef.current) {
      return undefined;
    }

    timeoutId = window.setTimeout(() => {
      if (!editorRef.current) {
        console.error('Monaco editor mount timed out; using textarea fallback.');
        setEditorLoadState('failed');
      }
    }, MONACO_MOUNT_TIMEOUT_MS);

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [editorLoadState, editorRenderKey, open]);

  const copyToClipboard = useCallback(async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(successMessage);
    } catch (error) {
      console.error('Clipboard write failed:', error);
      message.error(t('trainingProfiles.bulkImport.copyError', 'Failed to copy to clipboard'));
    }
  }, [t]);

  const handleEnableAdvancedEditor = useCallback(() => {
    if (editorLoadState === 'loading') {
      return;
    }

    editorRef.current = null;
    setEditorRenderKey((currentKey) => currentKey + 1);
    setEditorLoadState('loading');
  }, [editorLoadState]);

  const handleRetryEditor = useCallback(() => {
    editorRef.current = null;
    setEditorRenderKey((currentKey) => currentKey + 1);
    setEditorLoadState('loading');
  }, []);

  const handleEditorDidMount = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  const handleGenerateTemplate = useCallback(() => {
    setJsonValue(generateTrainingProfileImportTemplate(importContext));
  }, [importContext]);

  const handleLoadCurrentProfile = useCallback(() => {
    if (!profile) {
      return;
    }

    setJsonValue(generateTrainingProfileCurrentImportJson({ profile }));
    setSelectedQuestIndex(null);
  }, [profile]);

  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      setJsonValue(JSON.stringify(parsed, null, 2));
    } catch {
      message.warning(t('trainingProfiles.bulkImport.cannotFormat', 'Cannot format invalid JSON'));
    }
  }, [jsonValue, t]);

  const handleClear = useCallback(() => {
    setJsonValue('');
    setSelectedQuestIndex(null);
  }, []);

  const handleLocateQuest = useCallback((questIndex) => {
    setSelectedQuestIndex(questIndex);

    if (!editorRef.current || !jsonValue || !parseResult.parsedValue) {
      return;
    }

    const offsetRange = findQuestOffsetRange(jsonValue, parseResult.parsedValue, questIndex);
    if (!offsetRange) {
      return;
    }

    const model = editorRef.current.getModel();
    if (!model) {
      return;
    }

    const startPosition = model.getPositionAt(offsetRange.startOffset);
    const endPosition = model.getPositionAt(offsetRange.endOffsetExclusive);
    editorRef.current.revealRangeInCenter({
      startLineNumber: startPosition.lineNumber,
      startColumn: startPosition.column,
      endLineNumber: endPosition.lineNumber,
      endColumn: endPosition.column,
    });
    editorRef.current.setSelection({
      startLineNumber: startPosition.lineNumber,
      startColumn: startPosition.column,
      endLineNumber: endPosition.lineNumber,
      endColumn: endPosition.column,
    });
    editorRef.current.focus();
  }, [jsonValue, parseResult.parsedValue]);

  const handleImport = useCallback(async () => {
    if (!canImport || !parseResult.request) {
      return;
    }

    setImporting(true);
    try {
      const result = await trainingProfilesService.bulkImportTrainingProfile(profileId, parseResult.request);

      if (result.success) {
        message.success(
          t(
            'trainingProfiles.bulkImport.profileSuccess',
            'Imported {{questCount}} quests, {{stepCount}} steps, and {{quizCount}} quiz assignments',
            {
              questCount: result.importedQuestCount,
              stepCount: result.importedStepCount,
              quizCount: result.quizAssignmentCount,
            }
          )
        );
        onSuccess?.(result.profile);
        onClose();
        return;
      }

      const serverMessages = [
        ...(result.profileErrors || []),
        ...((result.questErrors || []).flatMap((questError) => (
          (questError.errors || []).map((entry) => `#${questError.index + 1}: ${entry}`)
        ))),
      ].filter(Boolean);

      Modal.error({
        title: t('trainingProfiles.bulkImport.importFailed', 'Import failed'),
        content: <div className={styles.serverErrorBody}>{serverMessages.join('\n') || t('trainingProfiles.bulkImport.unknownError', 'Unknown import error')}</div>,
        width: 720,
      });
    } catch (error) {
      console.error('Training profile AI import error:', error);
      const responseData = error.response?.data;
      const serverMessages = [
        ...(responseData?.profileErrors || []),
        ...((responseData?.questErrors || []).flatMap((questError) => (
          (questError.errors || []).map((entry) => `#${questError.index + 1}: ${entry}`)
        ))),
      ].filter(Boolean);

      if (serverMessages.length > 0) {
        Modal.error({
          title: t('trainingProfiles.bulkImport.importFailed', 'Import failed'),
          content: <div className={styles.serverErrorBody}>{serverMessages.join('\n')}</div>,
          width: 720,
        });
      } else {
        message.error(
          responseData?.message
          || error.message
          || t('trainingProfiles.bulkImport.unknownError', 'Unknown import error')
        );
      }
    } finally {
      setImporting(false);
    }
  }, [canImport, onClose, onSuccess, parseResult.request, profileId, t]);

  const renderEditor = () => {
    if (editorLoadState === 'failed') {
      return (
        <div className={styles.fallbackEditorLayout}>
          <Alert
            type="warning"
            showIcon
            message={t('trainingProfiles.bulkImport.editorFallbackTitle', 'Advanced editor unavailable')}
            description={t(
              'trainingProfiles.bulkImport.editorFallbackDescription',
              'The syntax-highlighting editor could not be loaded. You can still paste and import JSON using the plain text editor below.'
            )}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryEditor}>
                {t('trainingProfiles.bulkImport.retryEditor', 'Retry editor')}
              </Button>
            }
          />
          <TextArea
            value={jsonValue}
            onChange={(event) => setJsonValue(event.target.value)}
            spellCheck={false}
            className={styles.fallbackTextarea}
          />
        </div>
      );
    }

    if (editorLoadState !== 'ready') {
      return (
        <div className={styles.fallbackEditorLayout}>
          <Alert
            type="info"
            showIcon
            message={
              editorLoadState === 'loading'
                ? t('trainingProfiles.bulkImport.loadingEditorTitle', 'Loading advanced editor')
                : t('trainingProfiles.bulkImport.basicEditorTitle', 'Plain text editor')
            }
            description={t(
              editorLoadState === 'loading'
                ? 'trainingProfiles.bulkImport.loadingEditorDescription'
                : 'trainingProfiles.bulkImport.basicEditorDescription',
              editorLoadState === 'loading'
                ? 'You can keep editing while the Monaco editor initializes in the background.'
                : 'The plain text editor is ready now. Start the advanced editor if you want syntax highlighting and code navigation.'
            )}
            action={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleEnableAdvancedEditor}
                loading={editorLoadState === 'loading'}
              >
                {editorLoadState === 'loading'
                  ? t('trainingProfiles.bulkImport.loadingEditorButton', 'Loading...')
                  : t('trainingProfiles.bulkImport.enableEditor', 'Use advanced editor')}
              </Button>
            }
          />
          <TextArea
            value={jsonValue}
            onChange={(event) => setJsonValue(event.target.value)}
            spellCheck={false}
            className={styles.fallbackTextarea}
          />
        </div>
      );
    }

    return (
      <Editor
        key={editorRenderKey}
        height="100%"
        defaultLanguage="json"
        value={jsonValue}
        onChange={(value) => setJsonValue(value ?? '')}
        onMount={handleEditorDidMount}
        theme="vs-light"
        options={{
          minimap: { enabled: false },
          fontSize: 13,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          folding: true,
          bracketPairColorization: { enabled: true },
        }}
      />
    );
  };

  const renderQuestCard = useCallback((item) => (
    <div
      key={item.index}
      className={[
        styles.questCard,
        item.valid ? styles.questCardValid : styles.questCardInvalid,
        selectedQuestIndex === item.index ? styles.questCardSelected : null,
      ].filter(Boolean).join(' ')}
      onClick={() => handleLocateQuest(item.index)}
    >
      <div className={styles.questCardHeader}>
        <div className={styles.questCardTitleRow}>
          <Badge count={item.index + 1} style={{ backgroundColor: item.valid ? '#52c41a' : '#ff4d4f' }} showZero />
          <Text strong>{item.title}</Text>
        </div>
        <Space size={6}>
          <Tooltip title={t('trainingProfiles.bulkImport.locateQuestInEditor', 'Locate quest in editor')}>
            <Button
              type="text"
              size="small"
              icon={<AimOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                handleLocateQuest(item.index);
              }}
            />
          </Tooltip>
          {item.valid ? (
            <CheckCircleOutlined className={styles.questCardStatusOk} />
          ) : (
            <CloseCircleOutlined className={styles.questCardStatusError} />
          )}
        </Space>
      </div>

      <div className={styles.questCardMeta}>
        {item.category && <Tag color="blue">{item.category}</Tag>}
        {item.difficulty && <Tag color="purple">{item.difficulty}</Tag>}
        {item.estimatedTime && <Tag color="cyan">{item.estimatedTime}</Tag>}
        {item.isChallenge && <Tag color="volcano">{t('trainingProfiles.bulkImport.challenge', 'Challenge')}</Tag>}
      </div>

      <div className={styles.questCardStats}>
        <span>{item.stepCount} {t('trainingProfiles.bulkImport.steps', 'steps')}</span>
        <span>{item.spotlightCount} {t('trainingProfiles.bulkImport.spotlights', 'spotlights')}</span>
        <span>{item.answerCheckCount} {t('trainingProfiles.bulkImport.answerChecks', 'answer checks')}</span>
        <span>{item.conditionUsed ? t('trainingProfiles.bulkImport.conditionConfigured', 'condition set') : t('trainingProfiles.bulkImport.noCondition', 'no condition')}</span>
      </div>

      {item.iconKey && (
        <div className={styles.questCardIconRow}>
          <Text type="secondary">{t('trainingProfiles.bulkImport.icon', 'Icon')}:</Text>
          <Tag color={item.iconKnown ? 'default' : 'warning'}>{item.iconLabel}</Tag>
        </div>
      )}

      {item.stepTitles.length > 0 ? (
        <div className={styles.questCardSteps}>
          <Text type="secondary">{t('trainingProfiles.bulkImport.stepPreview', 'Step preview')}</Text>
          <ul className={styles.questCardStepList}>
            {item.stepTitles.map((stepTitle, stepIndex) => (
              <li key={`${item.index}-${stepIndex}`}>{stepTitle}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className={styles.questCardStepsEmpty}>
          {t('trainingProfiles.bulkImport.noStepsDefined', 'No steps defined yet')}
        </div>
      )}

      {!item.valid && item.errors.length > 0 && (
        <div className={styles.questCardErrors}>
          <Text type="danger" strong>{t('trainingProfiles.bulkImport.validationIssues', 'Validation issues')}</Text>
          <ul className={styles.questErrorList}>
            {item.errors.map((error, errorIndex) => (
              <li key={`${item.index}-error-${errorIndex}`}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  ), [handleLocateQuest, selectedQuestIndex, t]);

  const preview = parseResult.preview;

  return (
    <Modal
      title={
        <Space>
          <DownloadOutlined />
          {t('trainingProfiles.bulkImport.profileTitle', 'Populate training profile with AI')}
        </Space>
      }
      open={open}
      onCancel={onClose}
      width="98vw"
      style={{ top: 12, maxWidth: 1760 }}
      styles={{ body: { padding: '16px 24px', height: 'calc(100vh - 104px)', overflow: 'hidden' } }}
      footer={
        <div className={styles.modalFooter}>
          <Space>
            {parseResult.questValidationResults.length > 0 && (
              <>
                <Badge count={validQuestCount} style={{ backgroundColor: '#52c41a' }} showZero />
                <Text type="secondary">{t('trainingProfiles.bulkImport.valid', 'valid quests')}</Text>
              </>
            )}
            {totalErrorCount > 0 && (
              <>
                <Badge count={totalErrorCount} style={{ backgroundColor: '#ff4d4f' }} showZero />
                <Text type="secondary">{t('trainingProfiles.bulkImport.withErrors', 'with errors')}</Text>
              </>
            )}
          </Space>
          <Space>
            <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleImport} disabled={!canImport} loading={importing}>
              {importing
                ? t('trainingProfiles.bulkImport.importing', 'Importing...')
                : t('trainingProfiles.bulkImport.importProfileButton', 'Import whole profile')}
            </Button>
          </Space>
        </div>
      }
    >
      <div className={styles.bulkImportLayout}>
        <div className={styles.bulkImportEditor}>
          <div className={styles.bulkImportToolbar}>
            <Space size="small" wrap>
              <Tooltip title={t('trainingProfiles.bulkImport.templateTooltip', 'Insert a full profile template with example quests')}>
                <Button icon={<DownloadOutlined />} onClick={handleGenerateTemplate}>
                  {t('trainingProfiles.bulkImport.template', 'Template')}
                </Button>
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.loadCurrentProfileTooltip', 'Load the current training profile into the editor as import-ready JSON')}>
                <Button icon={<AppstoreOutlined />} onClick={handleLoadCurrentProfile} disabled={!profile}>
                  {t('trainingProfiles.bulkImport.loadCurrentProfile', 'Current profile JSON')}
                </Button>
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.copyTemplateTooltip', 'Copy template JSON for AI tools')}>
                <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(generateTrainingProfileImportTemplate(importContext), t('trainingProfiles.bulkImport.templateCopied', 'Template copied to clipboard'))} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.copyAiInstructionsTooltip', 'Copy the full AI authoring package')}>
                <Button icon={<BulbOutlined />} onClick={() => copyToClipboard(combinedAiPackage, t('trainingProfiles.bulkImport.aiInstructionsCopied', 'AI package copied to clipboard'))} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.copyAiReferenceTooltip', 'Copy the structured AI reference JSON')}>
                <Button icon={<CodeOutlined />} onClick={() => copyToClipboard(aiContextValue, t('trainingProfiles.bulkImport.aiReferenceCopied', 'AI reference copied to clipboard'))} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.formatTooltip', 'Format JSON')}>
                <Button icon={<FormatPainterOutlined />} onClick={handleFormatJson} disabled={!jsonValue} />
              </Tooltip>
              <Tooltip title={t('trainingProfiles.bulkImport.clearTooltip', 'Clear editor')}>
                <Button icon={<ClearOutlined />} onClick={handleClear} disabled={!jsonValue} />
              </Tooltip>
            </Space>
            <Text type="secondary" className={styles.characterCount}>
              {jsonValue.length.toLocaleString()} {t('trainingProfiles.bulkImport.chars', 'chars')}
            </Text>
          </div>

          <div className={styles.bulkImportEditorWrapper}>
            {renderEditor()}
          </div>

          {parseResult.error ? (
            <Alert
              type="error"
              showIcon
              message={t('trainingProfiles.bulkImport.invalidJson', 'Invalid JSON')}
              description={parseResult.error}
              className={styles.bulkImportStatus}
            />
          ) : parseResult.status !== 'empty' ? (
            <Alert
              type={parseResult.isValid ? 'success' : 'warning'}
              showIcon
              message={parseResult.isValid
                ? t('trainingProfiles.bulkImport.allValid', 'Profile import is valid')
                : t('trainingProfiles.bulkImport.profileErrors', 'Profile import needs fixes')}
              description={parseResult.isValid
                ? t('trainingProfiles.bulkImport.profileReady', 'The whole profile payload is ready to apply.')
                : [...parseResult.profileErrors, ...parseResult.questValidationResults.filter((result) => !result.valid).slice(0, 2).map((result) => `${result.title}: ${result.errors[0]}`)].join(' | ')}
              className={styles.bulkImportStatus}
            />
          ) : null}
        </div>

        <div className={styles.bulkImportPreview}>
          <Collapse
            className={styles.previewCollapse}
            ghost
            size="small"
            defaultActiveKey={['summary', 'schema', 'ai']}
            items={[
              {
                key: 'summary',
                label: (
                  <Space>
                    <AppstoreOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.profilePreviewTitle', 'Profile preview')}</Text>
                  </Space>
                ),
                children: preview ? (
                  <div className={styles.schemaCard}>
                    <div className={styles.profileSummaryGrid}>
                      <div className={styles.profileSummaryCard}>
                        <span>{t('trainingProfiles.name', 'Name')}</span>
                        <strong>{preview.name || t('trainingProfiles.editTitle', 'Training profile')}</strong>
                      </div>
                      <div className={styles.profileSummaryCard}>
                        <span>{t('trainingProfiles.configuration', 'Configuration')}</span>
                        <strong>{preview.populatedConfigurationCount} / {preview.configurationItemCount}</strong>
                      </div>
                      <div className={styles.profileSummaryCard}>
                        <span>{t('trainingProfiles.tutorialsTab', 'Tutorial quests')}</span>
                        <strong>{preview.questCount} / {preview.stepCount}</strong>
                      </div>
                      <div className={styles.profileSummaryCard}>
                        <span>{t('trainingProfiles.spotlightTargetLabel', 'Spotlights')}</span>
                        <strong>{preview.spotlightCount} / {preview.answerCheckCount}</strong>
                      </div>
                    </div>
                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.sectionBehaviorTitle', 'Section behavior')}</Text>
                      <div className={styles.tagWrap}>
                        <Tag color={preview.sectionsProvided.configuration ? 'blue' : 'default'}>{preview.sectionsProvided.configuration ? 'Configuration provided' : 'Configuration kept from current profile'}</Tag>
                        <Tag color={preview.sectionsProvided.tutorialQuests ? 'blue' : 'default'}>{preview.sectionsProvided.tutorialQuests ? 'Tutorial quests provided' : 'Tutorial quests kept from current profile'}</Tag>
                        <Tag>{preview.learningResourceCount} {t('trainingProfiles.resourcesCount', 'resources')} unchanged</Tag>
                        <Tag>{preview.conditionCount} {t('trainingProfiles.conditionAst', 'AST conditions')}</Tag>
                      </div>
                    </div>
                    {preview.additionalTextPreview && (
                      <Alert
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        message={t('trainingProfiles.description', 'Profile description')}
                        description={preview.additionalTextPreview}
                      />
                    )}
                    {parseResult.profileErrors.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('trainingProfiles.bulkImport.profileErrorList', 'Profile-level issues')}
                        description={(
                          <ul className={styles.questErrorList}>
                            {parseResult.profileErrors.map((error, index) => <li key={`profile-error-${index}`}>{error}</li>)}
                          </ul>
                        )}
                      />
                    )}
                  </div>
                ) : (
                  <div className={styles.bulkEmptyState}>
                    <DownloadOutlined className={styles.bulkEmptyIcon} />
                    <Text type="secondary">{t('trainingProfiles.bulkImport.emptyState', 'Paste JSON or insert the template to begin')}</Text>
                  </div>
                ),
              },
              {
                key: 'schema',
                label: (
                  <Space>
                    <SettingOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.schemaTitle', 'Schema guide')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.schemaCard}>
                    <div className={styles.schemaGrid}>
                      <div><Text code>Name</Text>, <Text code>AdditionalTextRichContent</Text>, <Text code>InformationRichContent</Text></div>
                      <div>{t('trainingProfiles.bulkImport.profileFieldsHelp', 'Top-level profile fields for the profile name, intro/description content, and learner-facing simulation information.')}</div>
                      <div><Text code>ConfigurationDraft</Text></div>
                      <div>{t('trainingProfiles.bulkImport.configDraftHelp', 'Preferred AI-friendly configuration shape keyed by item id with visible/editable/default/fixed state.')}</div>
                      <div><Text code>TutorialQuests</Text></div>
                      <div>{t('trainingProfiles.bulkImport.schemaSteps', 'Array of quest objects with nested step objects, AST conditions, spotlight targets, and optional expected values.')}</div>
                    </div>
                    <Alert
                      type="info"
                      showIcon
                      icon={<InfoCircleOutlined />}
                      message={t('trainingProfiles.bulkImport.nonImportedFeaturesTitle', 'Managed outside this JSON import')}
                      description={t('trainingProfiles.bulkImport.nonImportedFeaturesBody', 'Learning-resource file uploads, embedded rich-text images, ownership, and publication are still handled with their dedicated editor tools.')}
                    />
                  </div>
                ),
              },
              {
                key: 'configuration',
                label: (
                  <Space>
                    <AppstoreOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.configurationCatalogTitle', 'Configuration catalog')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.selectorList}>
                    {buildTrainingProfileAiContext(importContext).ConfigurationCatalog.map((group) => (
                      <div key={group.id} className={styles.selectorRow}>
                        <strong>{group.title}</strong>
                        <Text type="secondary">{group.description}</Text>
                        <div className={styles.tagWrap}>
                          {group.items.map((item) => (
                            <Tag key={item.id}>{item.id}</Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: 'conditions',
                label: (
                  <Space>
                    <CodeOutlined />
                    <Text strong>{t('trainingProfiles.conditionAstGuide', 'Condition AST guide')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.schemaCard}>
                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.astFieldsTitle', 'Allowed AST fields')}</Text>
                      <div className={styles.tagWrap}>
                        {CONDITION_FIELD_DEFINITIONS.map((field) => (
                          <Tag key={field.key}>{field.key}</Tag>
                        ))}
                      </div>
                    </div>
                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.astOperatorsTitle', 'Allowed AST operators')}</Text>
                      <div className={styles.tagWrap}>
                        {CONDITION_OPERATOR_DEFINITIONS.map((operator) => (
                          <Tag key={operator.key}>{operator.key}</Tag>
                        ))}
                      </div>
                    </div>
                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.astCombinatorsTitle', 'Combinators')}</Text>
                      <div className={styles.tagWrap}>
                        {CONDITION_COMBINATOR_DEFINITIONS.map((entry) => (
                          <Tag key={entry.key}>{entry.key}</Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'spotlight',
                label: (
                  <Space>
                    <BulbOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.spotlightLibraryTitle', 'Spotlight and step checks')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.schemaCard}>
                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.spotlightPositionTitle', 'Spotlight positions')}</Text>
                      <div className={styles.tagWrap}>
                        {SPOTLIGHT_POSITION_DEFINITIONS.map((position) => (
                          <Tag key={position.key}>{position.key}</Tag>
                        ))}
                      </div>
                    </div>
                    <div className={styles.knownValuesSection}>
                      <Text strong>{t('trainingProfiles.bulkImport.expectedValueSupportTitle', 'Step answer-check fields')}</Text>
                      <div className={styles.tagWrap}>
                        <Tag>AnswerChecks</Tag>
                        <Tag>LockNextStepsUntilCorrect</Tag>
                      </div>
                      <Text type="secondary">
                        {t('trainingProfiles.bulkImport.expectedValueSupportHint', 'Use AnswerChecks for ordered expected-value pairs, yes/no questions, and multiple-choice questions. Only set LockNextStepsUntilCorrect when at least one answer check is present.')}
                      </Text>
                    </div>
                    <div className={styles.selectorList}>
                      {buildTrainingProfileAiContext(importContext).Spotlight.selectors.slice(0, 18).map((selector) => (
                        <div key={selector.selector} className={styles.selectorRow}>
                          <strong>{selector.name}</strong>
                          <Text type="secondary">{selector.category}</Text>
                          <code>{selector.selector}</code>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                key: 'ai',
                label: (
                  <Space>
                    <BulbOutlined />
                    <Text strong>{t('trainingProfiles.bulkImport.aiTitle', 'AI instructions')}</Text>
                  </Space>
                ),
                children: (
                  <div className={styles.aiCard}>
                    <Paragraph className={styles.aiLead}>
                      {t('trainingProfiles.bulkImport.aiLeadProfile', 'Copy the AI package to give your external model the exact configuration catalog, AST rules, spotlight library, and current profile context.')}
                    </Paragraph>
                    <pre className={styles.aiPrompt}>{aiInstructions}</pre>
                    <div className={styles.aiActions}>
                      <Space wrap>
                        <Button icon={<CopyOutlined />} onClick={() => copyToClipboard(combinedAiPackage, t('trainingProfiles.bulkImport.aiInstructionsCopied', 'AI package copied to clipboard'))}>
                          {t('trainingProfiles.bulkImport.copyAiInstructions', 'Copy AI package')}
                        </Button>
                        <Button icon={<CodeOutlined />} onClick={() => copyToClipboard(aiContextValue, t('trainingProfiles.bulkImport.aiReferenceCopied', 'AI reference copied to clipboard'))}>
                          {t('trainingProfiles.bulkImport.copyAiReference', 'Copy AI reference JSON')}
                        </Button>
                      </Space>
                    </div>
                  </div>
                ),
              },
            ]}
          />

          {parseResult.questValidationResults.length > 0 ? (
            <div className={styles.questListSection}>
              <div className={styles.previewHeader}>
                <Space>
                  <DownloadOutlined />
                  <Text strong>{t('trainingProfiles.bulkImport.previewTitle', 'Quest preview')}</Text>
                </Space>
                <Text type="secondary">{t('trainingProfiles.bulkImport.previewWholeProfileHint', 'The template inserts full quest examples. Review the nested tutorial content before applying the import.')}</Text>
              </div>
              <div className={styles.questCardList}>
                {parseResult.questValidationResults.map((item) => renderQuestCard(item))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default TrainingProfileAiImportModal;